// Sets a device up for Wi-Fi printing over USB: Wi-Fi credentials, print server address, the printer key, and
// switches it to Wi-Fi mode; then waits until the print server has accepted the device ("ready").
// SET_SERVER, SET_KEY and SET_NETMODE each restart the device, so the page has to reconnect in between.

// The steps in the order they run; onStep(text) is called with one of these when it starts, so a UI can show
// the whole list from the beginning.
export const WIFI_SETUP_STEPS = [
  'Saving the Wi-Fi network',
  'Saving the print server address',
  'Saving the printer key',
  'Switching to Wi-Fi printing',
  'Connecting to the print server (the device joins the Wi-Fi network and checks in)',
];

// device: a DeviceLink, or a function returning the current one (it changes with every restart).
// reconnect(): waits for the restarted device and reopens the port (resolves true when it worked).
// onStep(text): progress for the UI. Resolves with the final status of the device (netStatus()); throws with a
// readable message if a step fails.
// keepWifi: the device already has a network stored and keeps it (ssid and password are not used, nothing is saved).
export async function applyWifiSetup({ getDevice, reconnect, ssid, password, keepWifi = false, serverUrl, key, onStep = () => {}, pollMs = 3000, timeoutMs = 120000, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), now = () => Date.now() }) {
  const step = async (text, fn, { restarts = false } = {}) => {
    onStep(text);
    let status;
    try {
      status = await fn(getDevice());
    } catch (e) {
      // An answer that never came (seen once on the real board): every step only stores a value, so asking again is safe.
      // The device may have restarted after saving: wait until it answers, then repeat.
      if (!/no answer/.test(e.message)) throw e;
      if (!(await reconnect())) throw new Error('the device does not answer; replug it and try again');
      status = await fn(getDevice());
    }
    if (status !== 0) throw new Error(`the device refused it: ${text}`);
    if (restarts && !(await reconnect())) throw new Error('the device did not come back after saving; replug it and try again');
  };
  if (keepWifi) onStep(WIFI_SETUP_STEPS[0]); // listed as done at once (the UI words it differently)
  else await step(WIFI_SETUP_STEPS[0], (d) => d.setWifi(ssid, password));
  await step(WIFI_SETUP_STEPS[1], (d) => d.setServer(serverUrl), { restarts: true });
  await step(WIFI_SETUP_STEPS[2], (d) => d.setKey(key), { restarts: true });
  await step(WIFI_SETUP_STEPS[3], (d) => d.setNetMode(true), { restarts: true });

  onStep(WIFI_SETUP_STEPS[4]);
  const t0 = now();
  let last = null;
  while (now() - t0 < timeoutMs) {
    await sleep(pollMs);
    try {
      last = await getDevice().netStatus();
    } catch {
      continue; // the device is busy with its radio for a moment: ask again
    }
    if (!last) continue;
    if (last.ready) return last;
    // the first check-in is still to come (it takes several seconds); a refusal is final
    if (last.error === 5) throw new Error('the print server does not accept this key');
  }
  throw new Error(`the device did not get ready in time${last ? `: ${last.errorText}` : ''}. Check the network name and password and the server address`);
}
